/**
 * MultiSelect Field Renderer
 *
 * A multi-select dropdown with search functionality
 * Uses the MultipleSelector component for a rich selection experience
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import type { FieldRenderer } from '@/MyCode/core';
import type { Option } from '@/components/ui/multiple-selector';
import MultipleSelector from '@/components/ui/multiple-selector';

/**
 * MultiSelect Renderer
 * Features:
 * - Search functionality
 * - Multiple selection
 * - Keyboard navigation
 * - Empty state message
 */
export const renderMultiSelect: FieldRenderer = ({
    field,
    value,
    handleChange,
    isRequired
}) => {
    const options = field.options?.items?.map((item) => ({
        value: item.value,
        label: item.label,
    })) || [];

    const selectedOptions: Option[] = Array.isArray(value)
        ? value.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return option || { value: val, label: val };
        })
        : [];

    const handleMultiSelectChange = (selected: Option[]) => {
        const values = selected.map((opt) => opt.value);
        handleChange(field.name, values);
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            {field.description && (
                <p className="text-sm text-muted-foreground">{field.description}</p>
            )}
            <MultipleSelector
                value={selectedOptions}
                onChange={handleMultiSelectChange}
                defaultOptions={options}
                placeholder={field.placeholder || 'Select options...'}
                emptyIndicator={
                    <p className="text-center text-sm text-muted-foreground">
                        No results found
                    </p>
                }
                commandProps={{
                    label: field.label,
                }}
            />
        </div>
    );
};

export default renderMultiSelect;
